import {
  RefObject,
  forwardRef,
  MutableRefObject,
  Ref,
  useEffect,
  useState,
  useRef,
} from "react"

import IMask from "imask"

import { TextField, TextFieldProps } from "../text-field"

export const MaskType = {
  Range: IMask.MaskedRange,
}

export type MaskedEvent = {
  value: string
  unmaskedValue?: string
  typedValue?: any
  name?: any
}

export type InputMask = {
  onAccept?: (event: MaskedEvent, ref: RefObject<HTMLInputElement>) => void
  onComplete?: (event: MaskedEvent, ref: RefObject<HTMLInputElement>) => void
}

export type TextFieldMaskedProps = {
  maskOptions: IMask.AnyMaskedOptions
  ref?: Ref<HTMLInputElement>
} & InputMask &
  TextFieldProps

export const TextFieldMasked = forwardRef<HTMLInputElement, TextFieldMaskedProps>(
  function TextFieldMasked(
    { name, value, onAccept, onComplete, maskOptions, ...rest },
    ref,
  ) {
    const internalRef = useRef<HTMLInputElement>()
    const inputRef = (ref as MutableRefObject<HTMLInputElement>) ?? internalRef

    const [mask, setMask] = useState<IMask.InputMask<IMask.AnyMaskedOptions> | null>(null)
    const [maskedValue, setMaskedValue] = useState<typeof value>(value ?? undefined)

    useEffect(() => {
      if (value !== undefined) {
        setMaskedValue(value)
      }
    }, [value])

    useEffect(() => {
      if (inputRef.current && !mask) {
        setMask(IMask(inputRef.current, { ...maskOptions }))
      }
    }, [mask, maskOptions])

    useEffect(() => {
      if (inputRef.current) {
        if (mask) {
          /** workaround for a known issue in the imask lib can be removed is 'mask.updateValue()' is fixed  */
          if (!onAccept) {
            mask.on("accept", () => {
              if (inputRef.current) {
                setMaskedValue(inputRef.current.value)
              }
            })
          }
          if (onAccept) {
            mask.on("accept", () => {
              if (inputRef.current) {
                setMaskedValue(inputRef.current.value)
                onAccept(
                  {
                    name,
                    value: inputRef.current.value,
                    unmaskedValue: mask.unmaskedValue,
                    typedValue: mask.typedValue,
                  },
                  inputRef,
                )
              }
            })
          }
          if (onComplete) {
            mask.on("complete", () => {
              if (inputRef.current) {
                onComplete(
                  {
                    name,
                    value: inputRef.current.value,
                    unmaskedValue: mask.unmaskedValue,
                    typedValue: mask.typedValue,
                  },
                  inputRef,
                )
              }
            })
          }
        }
      }
      return () => {
        if (mask) {
          mask.off("accept")
          mask.off("complete")
        }
      }
    }, [mask, name, onAccept, onComplete, value])

    return <TextField name={name} value={maskedValue} ref={inputRef} {...rest} />
  },
)
